import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import type { DateTimeFilter } from '../../../types/components/filter_bar_types';

interface ExpressBookSettings {
  minLeadTimeDays: number;
  maxLeadTimeDays: number;
  minEventDurationHours: number;
  maxEventDurationHours: number;
  minBufferTimeHours: number;
}

interface BookingWindow {
  dayOfWeek: string;
  startOffset: number;
  endOffset: number;
}

interface HoursOfOperationDay {
  dayOfWeek: string;
  isOpen: boolean;
  startTime: number;
  endTime: number;
}

interface SpaceWithUnavailableTimeSlots {
  expressBookSettings?: ExpressBookSettings | null;
  bookingWindows?: BookingWindow[];
  unavailableTimeSlots: {
    date: Dayjs;
    startOffset: number;
    endOffset: number;
  }[];
  venue: {
    hoursOfOperation: HoursOfOperationDay[];
    expressBookSettings: ExpressBookSettings;
  };
}

// Mirrors Space#effective_express_book_settings on the API.
const effectiveExpressBookSettings = (space: SpaceWithUnavailableTimeSlots) =>
  space.expressBookSettings ?? space.venue.expressBookSettings;

export const checkDateTimeFilter = (
  space: SpaceWithUnavailableTimeSlots,
  dateTimeFilter: DateTimeFilter | null,
): boolean => {
  if (!dateTimeFilter) {
    return true;
  }

  const { date, startOffset, endOffset } = dateTimeFilter;

  if (!date && !startOffset && !endOffset) {
    return true;
  }

  const expressBookSettings = effectiveExpressBookSettings(space);
  if (startOffset && endOffset) {
    const requestedEventDuration =
      (endOffset - startOffset) / 60;

    const isTooShort =
      requestedEventDuration <
      expressBookSettings.minEventDurationHours;

    const isTooLong =
      requestedEventDuration >
      expressBookSettings.maxEventDurationHours;

    if (isTooShort || isTooLong) {
      return false;
    }
  }

  // A space's booking windows replace the venue hours rather than narrowing
  // them, so the venue hours only decide for a space that keeps no windows.
  const keepsBookingWindows = (space.bookingWindows ?? []).length > 0;
  const isOpen =
    keepsBookingWindows || checkHoursOfOperation(space, dateTimeFilter);

  if (!isOpen) {
    return false;
  }

  const fitsBookingWindows = checkBookingWindows(
    space,
    dateTimeFilter,
    expressBookSettings.minEventDurationHours * 60,
  );

  if (!fitsBookingWindows) {
    return false;
  }

  if (!startOffset && !endOffset) {
    return true;
  }

  const maxLeadTime = expressBookSettings.maxLeadTimeDays;
  const maxLeadTimeDate = moment().add(maxLeadTime, 'days');
  const isWithinMaxLeadTime = date?.isBefore(
    maxLeadTimeDate.toDate(),
  );

  if (date && !isWithinMaxLeadTime) {
    return false;
  }

  const minLeadTime = expressBookSettings.minLeadTimeDays;
  const minLeadTimeDate = moment().add(minLeadTime, 'days');
  const isWithinMinLeadTime = date?.isAfter(
    minLeadTimeDate.toDate(),
  );

  if (date && !isWithinMinLeadTime) {
    return false;
  }

  const relevantUnavailableTimeSlots = space.unavailableTimeSlots.filter((t) =>
    dayjs(t.date).isSame(dateTimeFilter?.date?.toDate(), 'day'),
  );

  const minBufferTimeMinutes =
    expressBookSettings.minBufferTimeHours * 60;

  const isBlocked = !!relevantUnavailableTimeSlots.some((booking) => {
    return overlaps(booking, dateTimeFilter, minBufferTimeMinutes);
  });

  if (isBlocked) {
    return false;
  }

  return true;
};

const checkHoursOfOperation = (
  space: SpaceWithUnavailableTimeSlots,
  dateTimeFilter: DateTimeFilter,
) => {
  const { date, startOffset, endOffset } = dateTimeFilter;
  if (!date && startOffset && endOffset) {
    // check if the space is open at that time on any day of the week
    const isOpenThatTimeOnAnyDay = space.venue.hoursOfOperation.some(
      (venueAvailabilityDay) =>
        venueAvailabilityDay.isOpen &&
        venueAvailabilityDay.startTime <= startOffset &&
        venueAvailabilityDay.endTime >= endOffset,
    );
    return isOpenThatTimeOnAnyDay;
  }
  if (date) {
    const relevantHoursOfOperation = space.venue.hoursOfOperation.find(
      (venueAvailabilityDay) =>
        date?.format('dddd').toLowerCase() ===
        venueAvailabilityDay.dayOfWeek,
    );
    if (!relevantHoursOfOperation) {
      return false;
    }
    if (!startOffset || !endOffset) {
      return relevantHoursOfOperation.isOpen;
    }
    return relevantHoursOfOperation.isOpen &&
      relevantHoursOfOperation.startTime <= startOffset &&
      relevantHoursOfOperation.endTime >= endOffset;
    
  }
  return false;
};

// Mirrors BookingAvailabilities on the API. The API only sends the windows in
// effect, which are those of a space booking on its own settings. Such a space
// can only be booked inside its windows, and a day it left without one cannot
// be booked at all, whatever the venue hours say. A space with none falls back
// to the venue hours alone.
const checkBookingWindows = (
  space: SpaceWithUnavailableTimeSlots,
  dateTimeFilter: DateTimeFilter,
  minEventDurationMinutes: number,
) => {
  const bookingWindows = space.bookingWindows ?? [];
  if (bookingWindows.length === 0) {
    return true;
  }

  const { date, startOffset, endOffset } = dateTimeFilter;

  const fitsWindowOnDay = (dayOfWeek: string) => {
    const windows = bookingWindows.filter(
      (window) => window.dayOfWeek === dayOfWeek,
    );
    if (startOffset && endOffset) {
      return windows.some(
        (window) =>
          startOffset >= window.startOffset && endOffset <= window.endOffset,
      );
    }
    return windows.some(
      (window) =>
        window.endOffset - window.startOffset >= minEventDurationMinutes,
    );
  };

  if (date) {
    return fitsWindowOnDay(date.format('dddd').toLowerCase());
  }

  return bookingWindows.some((window) => fitsWindowOnDay(window.dayOfWeek));
};

const overlaps = (
  booking: {
    date: Dayjs;
    startOffset: number;
    endOffset: number;
  },
  dateTimeFilter: DateTimeFilter,
  minBufferTimeMinutes: number,
) => {
  const { date, startOffset, endOffset } = dateTimeFilter;
  if (!startOffset || !endOffset) {
    return false;
  }

  const isSameDay = dayjs(booking.date).isSame(date, 'day');

  const isBefore =
    booking.endOffset + minBufferTimeMinutes < startOffset;
  const isAfter =
    booking.startOffset - minBufferTimeMinutes > endOffset;

  const isOverlapping = !isBefore && !isAfter;

  return isSameDay && isOverlapping;
};
